import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { toAbsoluteUrl } from '../../../Utils/_helpers';
import './callpopup.css';
import "../../../Utils/_assets/sass/call-ticker.scss";
import { TelInput } from '../Ticker/TelInput';
import { WabaNumberSelect } from "./WabaNumberSelect";

import { useSnackbar } from "notistack";
import { openCallPopup } from './callPopupSlice';
import * as helpers from "../../util/helpers";

const CallPopup = ({ isOpen, onClose }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [callInProgress, setCallInProgress] = useState(false);
  const [callTimer, setCallTimer] = useState(0);
  const [socketConnected, setSocketConnected] = useState(false);
  const [callStatusText, setCallStatusText] = useState('Calling');
  const [businessNumber, setBusinessNumber] = useState(''); // Renamed for clarity
  const [isOptInSending, setIsOptInSending] = useState(false);

  const popupRef = useRef(null);
  const timerRef = useRef(null);
  const socketRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const offset = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const audioElement = useRef(null);
  const ringingAudioRef = useRef(null);
  const actionToPerformOnOpenRef = useRef(null); // 'sendOptIn' or 'setupCall'

  const isDuplicatedTab = useSelector(state => state.auth.is_duplicated_tab);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const plans = helpers.getPlanumbers();
  const srnumbers = getActivePhoneNumbers(plans.objects);
  const primarywabanumber = srnumbers.length > 0 ? srnumbers.pop() : "";

  useEffect(() => {
    // Set initial business number if not already set by WabaNumberSelect
    if (!businessNumber && primarywabanumber) {
        setBusinessNumber(primarywabanumber);
    }
  }, [primarywabanumber, businessNumber]);


  function getActivePhoneNumbers(plansArray) {
    if (!plansArray || !Array.isArray(plansArray)) {
      console.error("Invalid input: plansArray must be an array.");
      return [];
    }
    const activeNumbers = [];
    plansArray.forEach(planEntry => {
      if (planEntry.numbers && Array.isArray(planEntry.numbers)) {
        planEntry.numbers.forEach(numberDetails => {
          if (numberDetails.is_expired === false) {
            if (numberDetails.phone) {
              activeNumbers.push(numberDetails.phone);
            } else {
              console.warn("Found an active number entry without a phone property:", numberDetails);
            }
          }
        });
      }
    });
    return activeNumbers;
  }

  useEffect(() => {
    if (popupRef.current) {
      popupRef.current.style.right = '10px';
      popupRef.current.style.top = '120px';
    }
    audioElement.current = getAudio();
    ringingAudioRef.current = document.getElementById("ringingAudio");
    if (audioElement.current) {
      audioElement.current.autoplay = true;
    }
    if (ringingAudioRef.current) {
     ringingAudioRef.current.load();
   }
  }, []);

  useEffect(() => {
    if (!isPopupOpen || !popupRef.current) return;
    const popup = popupRef.current;
    const handleMouseDown = (e) => {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON' || e.target.tagName === 'A' || e.target.closest('.react-tel-input')) return;
        isDragging.current = true;
        offset.current = { x: e.clientX - popup.getBoundingClientRect().left, y: e.clientY - popup.getBoundingClientRect().top };
        popup.style.cursor = 'grabbing';
    };
    const handleMouseMove = (e) => {
        if (isDragging.current) {
            popup.style.left = `${e.clientX - offset.current.x}px`;
            popup.style.top = `${e.clientY - offset.current.y}px`;
            popup.style.right = 'auto';
        }
    };
    const handleMouseUp = () => {
        isDragging.current = false;
        popup.style.cursor = 'grab';
    };
    popup.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
        popup.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPopupOpen, isOpen]);


  useEffect(() => {
    const shouldTimerRun = callInProgress && ['Connected', 'Ringing','customer-joined'].includes(callStatusText);
    if (shouldTimerRun) {
      if (!timerRef.current) {
        timerRef.current = setInterval(() => setCallTimer((prev) => prev + 1), 1000);
      }
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [callInProgress, callStatusText]);

  useEffect(() => {
    if (message && (message.toLowerCase().includes('connected') || message.toLowerCase().includes('opt-in request sent') || message.toLowerCase().includes('opt-in confirmed'))) {
      const timeoutId = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [message]);

  const formatTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const getAudio = () => document.getElementById("remoteWCAudio");

  const stopRingingSound = () => {
   if (ringingAudioRef.current) {
       ringingAudioRef.current.pause();
       ringingAudioRef.current.currentTime = 0;
       console.log("Ringing sound stopped.");
   }
  };

  const startRingingSound = () => {
   if (ringingAudioRef.current) {
       ringingAudioRef.current.play().catch(error => {
           console.error("Error playing ringing sound:", error);
           setMessage("Could not play ringing sound automatically.");
       });
       console.log("Ringing sound started.");
   }
  };

  const handleHangUp = () => {
    console.log("HANLDEHANGUP: Disconnecting call and cleaning up resources...");
    stopRingingSound();

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ event: "disconnect", data: {} }));
    }

    if (socketRef.current) {
        console.log("HANLDEHANGUP: Clearing WebSocket handlers and closing.")
        socketRef.current.onopen = null;
        socketRef.current.onmessage = null;
        socketRef.current.onerror = null;
        socketRef.current.onclose = null; // Prevent generic onclose from firing
        if (socketRef.current.readyState === WebSocket.OPEN || socketRef.current.readyState === WebSocket.CONNECTING) {
            socketRef.current.close();
            console.log("HANLDEHANGUP: WebSocket connection explicitly closed.");
        }
    }
    socketRef.current = null;
    actionToPerformOnOpenRef.current = null;

    if (peerConnectionRef.current) {
        peerConnectionRef.current.onicecandidate = null;
        peerConnectionRef.current.ontrack = null;
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
        console.log("HANLDEHANGUP: RTCPeerConnection closed.");
    }

    if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
        console.log("HANLDEHANGUP: Local media stream tracks stopped.");
    }

    if (audioElement.current) {
        audioElement.current.pause();
        audioElement.current.srcObject = null;
        console.log("HANLDEHANGUP: Audio element paused and stream detached.");
    }

    setCallInProgress(false);
    setCallStatusText('Calling');
    setCallTimer(0);
    setSocketConnected(false);
    setIsOptInSending(false);
    console.log("HANLDEHANGUP: Call state reset.");
  };

  const handleClosePopup = () => {
   if (callInProgress) {
     onClose(); // Keep call active, just close popup UI
     return;
   }
   handleHangUp(); // If no call in progress, full cleanup
   setPhoneNumber('');
   setMessage('');
   onClose();
  };

  const setupPeerConnection = async (turnData) => {
    // ... (setupPeerConnection logic remains identical to your original code)
       console.log("Setting up RTCPeerConnection with TURN credentials");
        try {
            if (!turnData || !turnData.domain || !turnData.username || !turnData.password) {
                throw new Error("Invalid TURN credentials received.");
            }

            const iceServers = [
                {
                    urls: `turn:${turnData.domain}:3478?transport=udp`,
                    username: turnData.username,
                    credential: turnData.password,
                }
            ];

            if (peerConnectionRef.current) { peerConnectionRef.current.close(); }
            peerConnectionRef.current = new RTCPeerConnection({ iceServers });

            peerConnectionRef.current.onicecandidate = (event) => {
              console.log(event,"eventevent")
              if (event.candidate && socketRef.current?.readyState === WebSocket.OPEN && peerConnectionRef.current?.signalingState !== 'closed') {
                console.log("3. Sending ICE Candidate:", event.candidate);
                socketRef.current.send(JSON.stringify({ event: "trickle", data: { candidate: event.candidate } }));
              } else if (!event.candidate && socketRef.current?.readyState === WebSocket.OPEN && peerConnectionRef.current?.signalingState !== 'closed'){
                console.log("4. ICE Candidate Gathering Complete");
                socketRef.current.send(JSON.stringify({ event: "trickle-complete", data: {} }));
              }
            };
            peerConnectionRef.current.ontrack = (event) => {
              console.log("Remote track received:", event.streams[0]);
               if (audioElement.current && event.streams[0] && peerConnectionRef.current?.signalingState !== 'closed') {
                audioElement.current.srcObject = event.streams[0];
                console.log("Attached remote stream to audio element.");
              }
            };
            peerConnectionRef.current.onicecandidateerror = (event) => {
              console.error(
                  `ICE Candidate Error: Address: ${event.address}, Port: ${event.port}, URL: ${event.url}, Error Code: ${event.errorCode}, Error Text: ${event.errorText}`
              );
          };

          peerConnectionRef.current.oniceconnectionstatechange = () => {
            if (!peerConnectionRef.current) return; 
            const currentState = peerConnectionRef.current.iceConnectionState;
            console.log(`ICE Connection State Change: ${currentState}`);
            if(currentState==='disconnected'){
              console.warn("ICE connection disconnected. This might recover or lead to failure.");
            }
          }
          peerConnectionRef.current.onconnectionstatechange = () => {
            if (!peerConnectionRef.current) return;
            const overallState = peerConnectionRef.current.connectionState;
            console.log(`Overall PeerConnection State Change: ${overallState}`);
            if(overallState==='failed'){
              setMessage('Error: Call failed. Please verify network settings and try again.');
              handleHangUp();
            }
          }
            if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(track => track.stop()); }
            localStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            localStreamRef.current.getTracks().forEach((track) => {
                if (peerConnectionRef.current && peerConnectionRef.current.signalingState !== 'closed') {
                     peerConnectionRef.current.addTrack(track, localStreamRef.current);
                     console.log("Added local audio track:", track.kind);
                }
            });

          if (peerConnectionRef.current?.signalingState !== 'stable' && peerConnectionRef.current?.signalingState !== 'have-local-offer' && peerConnectionRef.current?.signalingState !== 'have-remote-offer') {
               console.warn(`Attempting to create offer in unexpected state: ${peerConnectionRef.current?.signalingState}`);
          }
          const offer = await peerConnectionRef.current.createOffer();
           if (peerConnectionRef.current?.signalingState === 'closed') { throw new Error("PC closed before offer set."); }
          await peerConnectionRef.current.setLocalDescription(offer);
          console.log("2. Local Description (Offer) Set. Sending 'configure' event.");

           if (socketRef.current?.readyState !== WebSocket.OPEN) { throw new Error("WS closed before configure sent."); }
          socketRef.current.send(JSON.stringify({
              event: "configure",
              data: { offer_sdp: offer, business_number: businessNumber.startsWith('+')? businessNumber.substring(1):businessNumber, customer_number: phoneNumber.replace(/\D/g, ''), other_data: { client_type: "react_popup_v1" } }
          }));

        } catch (error) {
              console.error("Error during WebRTC setup/Offer:", error);
              setMessage(`Error: ${error.message || 'Failed call setup.'}`);
              handleHangUp();
        }
  };

  const initializeNewSocket = () => {
    if (socketRef.current) {
        console.log("INITIALIZENEWSOCKET: Cleaning up existing socket before creating new.");
        socketRef.current.onopen = null;
        socketRef.current.onmessage = null;
        socketRef.current.onerror = null;
        socketRef.current.onclose = null;
        if (socketRef.current.readyState !== WebSocket.CLOSED) {
            socketRef.current.close();
        }
        socketRef.current = null;
    }

    console.log(`INITIALIZENEWSOCKET: Initializing. Action on open: ${actionToPerformOnOpenRef.current}`);
    socketRef.current = new WebSocket("wss://whatsapp-obd.knowlarity.com/outbound/v1/ws");

    socketRef.current.onopen = async () => {
        if (!socketRef.current) {
            console.warn("WS ONOPEN: but ref cleared.");
            actionToPerformOnOpenRef.current = null;
            return;
        }
        console.log("WS ONOPEN: Connected. Action to perform:", actionToPerformOnOpenRef.current);
        setSocketConnected(true);

        const action = actionToPerformOnOpenRef.current;
        actionToPerformOnOpenRef.current = null; // Consume action

        if (action === 'sendOptIn') {
            setIsOptInSending(false);
            const customer_number_formatted = phoneNumber.replace(/\D/g, '');
            const business_number_formatted = businessNumber.startsWith('+') ? businessNumber.substring(1) : businessNumber;;

            if (!customer_number_formatted || !business_number_formatted) {
                console.error("WS ONOPEN: Missing numbers for opt-in.");
                setMessage("Error: Missing numbers for opt-in. Cannot send.");
                return;
            }
            const optInPayload = {
                event: "opt-in",
                data: { customer_number: customer_number_formatted, business_number: business_number_formatted }
            };
            console.log("WS ONOPEN: Sending opt-in message.", optInPayload);
            if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                socketRef.current.send(JSON.stringify(optInPayload));
                setMessage('Opt-in request sent.');
            } else {
                setMessage("Error: Could not send opt-in. Connection issue post-open.");
            }
        } else if (action === 'setupCall') {
            setCallStatusText('Connecting');
            console.log("WS ONOPEN: Requesting TURN credentials for call...");
            if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                socketRef.current.send(JSON.stringify({ event: "get_turn_credentials", data: {} }));
            } else {
                setMessage("Error: WebSocket connection failed before call setup in onopen.");
                if (callInProgress) handleHangUp();
            }
        }
    };

    socketRef.current.onmessage = async (event) => {
        if (!socketRef.current) { console.warn("WS ONMESSAGE: but ref cleared."); return; }
        try {
            const messageData = JSON.parse(event.data);
            console.log("WS ONMESSAGE: Event:", messageData.event, "Data:", messageData.data);

            // Opt-in specific message handling (example)
            if (messageData.event === "opt-in-status" || messageData.event === "opt_in_status") {
                const currentCustomerNumber = phoneNumber.replace(/\D/g, '');
                if (messageData.data && messageData.data.customer_number === currentCustomerNumber) {
                    if (messageData.data.success) {
                        enqueueSnackbar("Opt-in successful!", { variant: "success" });
                        setMessage("Opt-in confirmed by server.");
                    } else {
                        const errorMsg = messageData.data.message || 'Unknown reason';
                        enqueueSnackbar(`Opt-in failed: ${errorMsg}`, { variant: "error" });
                        setMessage(`Opt-in server error: ${errorMsg}`);
                    }
                }
                 return; // Assuming opt-in status doesn't need further processing by call logic
            }


            if (!peerConnectionRef.current && ['configured', 'trickle', 'joined', 'ringing', 'customer-joined'].includes(messageData.event)) {
                if (messageData.event !== 'turn_credentials') {
                   console.warn(`WS ONMESSAGE: Received ${messageData.event} but PC closed/null. Ignoring.`); return;
                }
            }

            switch (messageData.event) {
              case "turn_credentials":
                console.log("WS ONMESSAGE: Received TURN credentials:", messageData.data);
                setCallStatusText('Calling');
                await setupPeerConnection(messageData.data);
                break;
              case "configured":
                console.log("WS ONMESSAGE: Call Configured by server:", messageData.data);
                if (messageData.data?.data?.jsep && messageData.data.data.jsep.type === 'answer') {
                  try {
                      if (peerConnectionRef.current && peerConnectionRef.current?.signalingState === 'have-local-offer') {
                         await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(messageData.data.data.jsep));
                         console.log("WS ONMESSAGE: Remote description set.");
                      } else { console.warn(`WS ONMESSAGE: Cannot set remote description in state: ${peerConnectionRef.current?.signalingState}. Ignoring answer.`); }
                  } catch(e) { console.error("WS ONMESSAGE: Error setting remote description:", e); setMessage("Error processing answer."); handleHangUp(); }
                } else { console.warn("WS ONMESSAGE: Received 'configured' event without valid SDP answer."); }
                break;
              case "ringing":
                console.log("WS ONMESSAGE: Call is Ringing:", messageData.data);
                setCallStatusText('Ringing');
                startRingingSound();
                setMessage('');
                break;
              case "customer-joined":
                console.log("WS ONMESSAGE: customer-joined:", messageData.data);
                stopRingingSound();
                setCallStatusText('Connected');
                setMessage('Call connected');
                break;
              case "error":
                console.error("WS ONMESSAGE: Received error event from WebSocket:", messageData.data);
                let extractedErrorMessage = "An error occurred during the call attempt.";
                if (messageData.data?.data?.error?.message) { extractedErrorMessage = messageData.data.data.error.message; }
                else if (messageData.data?.message) { extractedErrorMessage = messageData.data.message; }
                setMessage(`Error: ${extractedErrorMessage}`);
                handleHangUp();
                break;
              case "peer-leaving":
              case "call-terminated":
              case "customer-left":
              case "customer-declined":
                console.log("WS ONMESSAGE: Peer Leaving/Call Terminated:", messageData.event, messageData.data);
                setMessage('Call ended.');
                handleHangUp();
                break;
              case "trickle":
                if (messageData.data?.candidate && peerConnectionRef.current && peerConnectionRef.current.signalingState !== 'closed') {
                    console.log("WS ONMESSAGE: Received ICE Candidate from peer:", messageData.data.candidate);
                    try { await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(messageData.data.candidate)); }
                    catch (error) { if (error.name !== 'InvalidStateError') { console.error("WS ONMESSAGE: Error adding ICE candidate:", error); } else { console.warn("WS ONMESSAGE: Ignoring ICE candidate in invalid state."); } }
                }
                break;
              default:
                console.log("WS ONMESSAGE: Unhandled WebSocket event:", messageData.event, messageData.data);
            }
          } catch (error) {
           console.error("WS ONMESSAGE: Error processing WebSocket message:", error, "Raw data:", event.data);
           if(callInProgress) handleHangUp(); else setMessage("Error processing server message.");
         }
    };

    socketRef.current.onerror = (error) => {
        const currentHandlingSocket = socketRef.current; // Capture ref at time of error
        if (currentHandlingSocket) {
            console.error("WS ONERROR: WebSocket Network/Connection Error:", error);
            setMessage("WebSocket connection error.");
            setSocketConnected(false);
            setIsOptInSending(false);
            if (callInProgress) { // If a call was active, it has failed.
                handleHangUp();
            }
            // If this socket was the current one, nullify it as it's errored.
            // This check ensures we don't nullify a newer socket if onerror is delayed.
            if (socketRef.current === currentHandlingSocket) {
                socketRef.current = null;
            }
        } else {
            console.warn("WS ONERROR: but ref cleared or for an old instance.");
        }
        actionToPerformOnOpenRef.current = null;
    };

    socketRef.current.onclose = (event) => {
        const wasCallInProgress = callInProgress;
        const closedSocketInstance = socketRef.current; // The instance this onclose belongs to (potentially)

        console.log("WS ONCLOSE: Closed. Code:", event.code, "Reason:", event.reason, "Was call in progress:", wasCallInProgress);
        setSocketConnected(false);
        setIsOptInSending(false);

        actionToPerformOnOpenRef.current = null;

        // If the socket that just closed is still the one in socketRef, it means handleHangUp()
        // (which nulls socketRef.current) hasn't been called or this is an unexpected close.
        if (socketRef.current === closedSocketInstance) {
            if (wasCallInProgress) {
                if (!message || !message.toLowerCase().includes('error')) {
                    setMessage("Call disconnected unexpectedly.");
                }
                handleHangUp(); // Full cleanup for the call
            } else {
                // Socket closed, no call was active.
                if (event.code !== 1000 && event.code !== 1005) { // 1000 = normal, 1005 = no status
                     setMessage("WebSocket connection closed unexpectedly.");
                }
            }
            socketRef.current = null; // Ensure it's nulled if not already by hangup
        } else {
            console.log("WS ONCLOSE: Event for an old/already replaced socket instance or hangup already handled.");
        }
    };
  };

  const handleSendOptIn = async () => {
    if (phoneNumber.trim() === '' || !businessNumber || businessNumber.trim() === '') {
        setMessage('Phone number and a selected business number are required for opt-in.');
        enqueueSnackbar('Phone number and a selected business number are required for opt-in.', { variant: "warning" });
        return;
    }
    if (isOptInSending || (socketRef.current && socketRef.current.readyState === WebSocket.CONNECTING)) {
        setMessage('Opt-in request already in progress or socket connecting...');
        enqueueSnackbar('Opt-in request already in progress or socket connecting...', { variant: "info" });
        return;
    }

    setIsOptInSending(true);
    setMessage('Processing opt-in request...');

    const customer_number_formatted = phoneNumber.replace(/\D/g, '');
    const business_number_formatted = businessNumber.startsWith('+') ? businessNumber.substring(1) : businessNumber;

    const optInPayload = {
        event: "opt-in",
        data: { customer_number: customer_number_formatted, business_number: business_number_formatted }
    };

    actionToPerformOnOpenRef.current = 'sendOptIn';

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        console.log("HANDLESENDOPTIN: Socket open. Sending opt-in.");
        socketRef.current.send(JSON.stringify(optInPayload));
        setMessage('Opt-in request sent.');
        setIsOptInSending(false);
    } else {
        console.log("HANDLESENDOPTIN: Socket not open. Initializing for opt-in.");
        initializeNewSocket();
    }
  };

  const handleCall = async () => {
    if (phoneNumber.trim() === '') {
        setMessage('Please enter a valid phone number.');
        enqueueSnackbar('Please enter a valid phone number.', { variant: "warning" });
        return;
    }
    if (!businessNumber || businessNumber.trim() === '') {
        setMessage('A business number must be selected to make a call.');
        enqueueSnackbar('A business number must be selected to make a call.', { variant: "warning" });
        return;
    }

    if (callInProgress || (peerConnectionRef.current && peerConnectionRef.current.signalingState !== 'closed')) {
        setMessage("Call already in progress or finishing up. Please hang up first or wait.");
        enqueueSnackbar("Call already in progress or finishing up. Please hang up first or wait.", { variant: "info" });
        return;
    }
    
    if (peerConnectionRef.current) { // If a peer connection (even closed) exists from a *previous call*
        console.log("HANDLECALL: Previous peer connection found. Cleaning up with handleHangUp.");
        handleHangUp(); 
        await new Promise(resolve => setTimeout(resolve, 100)); // Allow full cleanup
    }

    setCallInProgress(true); // Set early
    setCallStatusText('Initializing');
    setCallTimer(0);
    setMessage('');

    actionToPerformOnOpenRef.current = 'setupCall';

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        console.log("HANDLECALL: Socket open. Requesting TURN.");
        setSocketConnected(true);
        setCallStatusText('Connecting');
        socketRef.current.send(JSON.stringify({ event: "get_turn_credentials", data: {} }));
    } else {
        console.log("HANDLECALL: Socket not open. Initializing for call.");
        initializeNewSocket();
    }
  };

  useEffect(() => {
   setIsPopupOpen(isOpen);
  }, [isOpen]);

  const onWabaNumberChange = (event) => { // Renamed for clarity
   setBusinessNumber(event.value);
  };

  return (
    <div>
      <audio id="remoteWCAudio" playsInline></audio>
      <audio id="ringingAudio" src={"https://webrtc-audio.knowlarity.com/tring_3_times.mp3"} preload="auto" loop></audio>

      {callInProgress && ['Connected', 'Ringing', 'customer-joined'].includes(callStatusText) && !isOpen && (
      <div
        className={`whatsapp-icon`}
        title="Open Call Popup"
        onClick={() => dispatch(openCallPopup())}
        style={{ cursor: 'pointer' }}
      >
          <div className="call-timer-cover">     
             {callTimer > 0 ? formatTime(callTimer) : ''}
          </div>
      </div>
   )}  
      {isOpen && (
        <div className="popup-overlay">
          <div className="popup-container" ref={popupRef} style={{ cursor: 'grab' }}>
            <div className="c2c_widget_header widget_header ctc-header">
               <div className="header-content">
                    <div className="oval"><span className="brand-logo1"><img alt="" className="brand-logo" src={toAbsoluteUrl("/media/sr/logos/menu-brand-logo.svg")} /></span></div>
                    <div className="heading">Whatsapp Call</div>
                </div>
            </div>
            {callInProgress && ['Connected', 'Ringing', 'customer-joined'].includes(callStatusText) ? 
              <span className="popup-close" title="Minimize" onClick={handleClosePopup} style={{ cursor: 'pointer' }}><img src={toAbsoluteUrl("/media/sr/icons/ticker-minimize.svg")} alt="Minimize" /></span>
            :
            <span className="popup-close" title="Close" onClick={handleClosePopup} style={{ cursor: 'pointer' }}><img width="20px" src={toAbsoluteUrl("/media/sr/icons/close-red.svg")} alt="Close" /></span>
            }
            {!callInProgress ? (
              <div style={{ padding: '20px' }}>
                <TelInput onEvent={(e) => setPhoneNumber(e.value)} status={0} ticker_initial_customer_number={phoneNumber} moduleName="whatsapp_ticker" showValidationMessage={true} />
                <div className="opc-select demo-wrapper" style={{ marginTop: '20px' }}>
                <WabaNumberSelect onEvent={onWabaNumberChange} initial_waba_number={businessNumber || primarywabanumber} />
               </div>
               {false &&
                <div className="col-12 dial-call-section" style={{ display: "flex", justifyContent: "center", marginTop: '20px'}}>
                  <a 
                    className=""
                    href="/#"
                    style={{width:"50%",
                        pointerEvents: (phoneNumber.trim().length < 5 || !businessNumber || businessNumber.trim() === '' || isOptInSending || (socketRef.current && socketRef.current.readyState === WebSocket.CONNECTING) ) ? "none" : "all",
                        opacity: (phoneNumber.trim().length < 5 || !businessNumber || businessNumber.trim() === '' || isOptInSending || (socketRef.current && socketRef.current.readyState === WebSocket.CONNECTING) ) ? 0.6 : 1,
                        marginRight: '10px', backgroundColor: '#2c3687', padding: '10px 15px', color: 'white', textDecoration: 'none', borderRadius: '20px', fontSize:'14px'
                    }}
                    onClick={(e) => { e.preventDefault(); handleSendOptIn(); }}
                    title="Send Opt-in request to the customer"
                  >
                    {isOptInSending || (actionToPerformOnOpenRef.current === 'sendOptIn' && socketRef.current && socketRef.current.readyState === WebSocket.CONNECTING) ? 'Sending...' : 'Send Opt-in'}
                  </a>
                  <a 
                    className=""
                    href="/#"
                    style={{width:"50%",
                        pointerEvents: "none",
                        opacity: 0.6,
                        marginRight: '10px', backgroundColor: '#2c3687', padding: '10px 15px', color: 'white', textDecoration: 'none', borderRadius: '20px', fontSize:'14px'
                    }}
                    onClick={(e) => {"" }}
                    title="check Opt-in request"
                  >
                    {'Check Opt-in'}
                  </a>
                </div>
                 }
                <div className="col-12 dial-call-section" style={{ display: "flex", justifyContent: "center"}}>
                    <a className="call-btn-c2c dddial-call-btn click-icon" href="/#" style={{ display: "flex", justifyContent: "center", alignItems: "center", pointerEvents: phoneNumber.trim().length < 5 ? "none" : "all", opacity: phoneNumber.trim().length < 5 ? 0.6 : 1 }} onClick={(e) => { e.preventDefault(); if (phoneNumber.trim().length >= 5) handleCall(); }}> {/* Basic length validation */}
                        <img alt="" className="dial-call-icon initial-dilar" style={{ pointerEvents: "all", marginRight: '5px' }} src={toAbsoluteUrl("/media/sr/icons/dial-call-white.svg")} /> Call
                    </a>
                </div>
                {message && ( <div className={`message ${message.toLowerCase().includes('error') ? 'error' : 'info'}`} style={{ marginTop: '15px', textAlign: 'center', wordBreak: 'break-word' }}>{message}</div> )}
              </div>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                 <h2>{['Calling', 'Ringing', 'Connected', 'customer-joined'].includes(callStatusText) ? `${callStatusText}: ${phoneNumber}` : callStatusText}</h2>
                 {['Connected', 'Ringing','customer-joined'].includes(callStatusText) && (
                    <div style={{ fontSize: '24px', margin: '10px 0 20px 0' }}>{formatTime(callTimer)}</div>
                 )}
                 {['Initializing', 'Connecting', 'Calling'].includes(callStatusText) && !['Connected', 'Ringing','customer-joined'].includes(callStatusText) && (
                     <div style={{ fontSize: '16px', margin: '10px 0 20px 0', fontStyle: 'italic', color: '#666' }}>{callStatusText}...</div>
                 )}
                 {message && !message.toLowerCase().includes('connected') && !message.toLowerCase().includes('call ended') && !message.toLowerCase().includes('disconnected') && (
                    <div className={`message ${message.toLowerCase().includes('error') ? 'error' : 'info'}`} style={{ marginBottom: '15px' }}>{message}</div>
                 )}
                <button onClick={handleHangUp} style={{ backgroundColor: 'red', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', marginTop: '10px' }}> Hang Up </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CallPopup;